import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectArticleId } from '../selectors/editorSelectors';
import InlineFeedback from 'ai-components-ui-library/components/feedback/InlineFeedback';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { Text } from 'foundations-components/components/Text';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { usageTracker } from '../../utils/usageTracker';
import { useGetSuggestionsFeedbackByArticleIdQuery, useCreateSuggestionsFeedbackMutation } from '../../../__generated__/chirp/com/hubspot/knowledge/base/agent/rpc/SuggestionsFeedbackRpc.hooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const optionsMap = {
  incorrectAction: 'NOT_DETAILED_ENOUGH',
  notRelevant: 'NOT_RELEVANT',
  inaccurate: 'NOT_FACTUALLY_CORRECT',
  other: 'OTHER',
  incomplete: 'OTHER'
};
export const AiArticleFeedback = () => {
  const articleId = useSelector(selectArticleId);
  const [initialVote, setInitialVote] = useState(undefined);
  const [isFeedbackDisabled, setIsFeedbackDisabled] = useState(false);
  const [createSuggestionsFeedback] = useCreateSuggestionsFeedbackMutation({
    useLegacyFieldNameMode: true
  });
  const {
    data: existingSuggestionsFeedback,
    loading: suggestionsFeedbackLoading
  } = useGetSuggestionsFeedbackByArticleIdQuery({
    variables: {
      articleId
    },
    skip: !articleId,
    useLegacyFieldNameMode: true
  });
  useEffect(() => {
    if (existingSuggestionsFeedback !== null && existingSuggestionsFeedback !== void 0 && existingSuggestionsFeedback.getSuggestionsFeedbackByArticleId) {
      setInitialVote(existingSuggestionsFeedback.getSuggestionsFeedbackByArticleId.isHelpful ? 1 : -1);
      setIsFeedbackDisabled(true);
    }
  }, [existingSuggestionsFeedback]);
  const handleSubmitFeedback = useCallback(async body => {
    try {
      await createSuggestionsFeedback({
        variables: body
      });
      setIsFeedbackDisabled(true);
    } catch (err) {
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: err instanceof Error ? err.message : 'An error occurred'
      });
    }
  }, [createSuggestionsFeedback]);
  const submitNegativeFeedback = useCallback(({
    vote,
    feedbackMessage = '',
    options = [],
    messageId
  }) => {
    const body = {
      isHelpful: vote < 0,
      feedbackText: feedbackMessage,
      articleId: Number(messageId),
      relevanceValues: options === null || options === void 0 ? void 0 : options.map(option => optionsMap[option])
    };
    void handleSubmitFeedback(body);
  }, [handleSubmitFeedback]);
  const submitPositiveFeedback = useCallback(({
    vote,
    messageId
  }) => {
    const body = {
      isHelpful: vote > 0,
      feedbackText: '',
      articleId: Number(messageId),
      relevanceValues: ['OTHER']
    };
    void handleSubmitFeedback(body);
  }, [handleSubmitFeedback]);
  return !suggestionsFeedbackLoading ? /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "row",
    gap: "300",
    children: [/*#__PURE__*/_jsx(Box, {
      grow: "1",
      children: /*#__PURE__*/_jsx(Text, {
        as: "p",
        whiteSpace: "nowrap",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "kbAgent.aiGenerationFeedback.label"
        })
      })
    }), /*#__PURE__*/_jsx(Box, {
      grow: "0",
      marginBlockStart: "-100",
      children: /*#__PURE__*/_jsx(InlineFeedback, {
        messageId: articleId,
        usageTracker: usageTracker,
        onNegativeFeedback: submitNegativeFeedback,
        onPositiveFeedback: submitPositiveFeedback,
        initialVote: initialVote,
        isFeedbackDisabled: isFeedbackDisabled
      })
    })]
  }) : /*#__PURE__*/_jsx(LoadingSpinner, {
    "aria-label": "Loading feedback"
  });
};